import { Check } from "lucide-react"
import { Link } from "react-router-dom"
import { gql } from "~/__generated__"
import { useViewer } from "~/auth/use-viewer"
import { useCopyShareLink } from "./use-copy-share-link"
import share from "~/images/share"
import { GroupIndexItemFragment } from "~/__generated__/graphql"
import { groupDetailPath } from "~/common/paths"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Button } from "~/ui/button"
import { useToast } from "~/ui/use-toast"
import { UserAvatar } from "~/users/user-avatar"
import { JOIN_GROUP_FROM_INDEX_MUTATION } from "./group-mutations"
import clsx from "clsx"
import { FlashMessageType, useFlashMessage } from "~/common/flash-message-context"

gql(`
  fragment GroupIndexItem on Group {
    id
    name
    description
    membersCount
    bookmarksCount
    bookmarksVisibleCount
    shareToken
    viewerIsMember
    visibility
    creator {
      ...UserAvatar
      fullName
    }
  }
`)

type GroupIndexItemProps = {
  group: GroupIndexItemFragment
  showFooter?: boolean
}

const InviteButton = ({
  group,
  className,
}: {
  group: GroupIndexItemFragment
  className: string
}) => {
  const { viewer } = useViewer()
  const copyShareLink = useCopyShareLink()

  return (
    <Button
      variant="ghost"
      size="sm"
      className={clsx("hover:bg-transparent", className)}
      onClick={(e) => {
        e.preventDefault()
        copyShareLink(group.shareToken, viewer.id)
      }}
    >
      Invite Friends
      <img {...share} className="ms-2" />
    </Button>
  )
}

const JoinButton = ({ group }: { group: GroupIndexItemFragment }) => {
  const { toast } = useToast()
  const { showFlashMessage } = useFlashMessage()

  const [joinGroup] = useSafeMutation(JOIN_GROUP_FROM_INDEX_MUTATION, {
    refetchQueries: ["GroupsQuery"],
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      })
    },
  })

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={async () => {
        const { errors } = await joinGroup({
          variables: {
            input: {
              shareToken: group.shareToken,
            },
          },
        })

        if (!errors) {
          showFlashMessage({
            message: `You've joined the ${group.name} Group. You can now add bookmarks to this group and will receive updates when others add theirs as well.`,
            type: FlashMessageType.success,
          })
        }
      }}
    >
      Join
    </Button>
  )
}

export const GroupIndexItem = ({ group, showFooter = true }: GroupIndexItemProps) => {
  return (
    <Link
      to={groupDetailPath({ id: group.id })}
      className="flex h-full flex-col rounded-sm border bg-white p-4 transition-colors hover:border-gray-400"
    >
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <UserAvatar user={group.creator} className="h-6 w-6 text-xs" />
          <div>
            <div className="text-xs text-subdued">
              by <span className="text-foreground">{group.creator.fullName}</span>
            </div>
          </div>
        </div>
        <div className="mt-2">
          <h3 className="truncate text-sm font-semibold">{group.name}</h3>
          {group.description && (
            <p className="mt-2 line-clamp-2 text-xs font-medium text-subdued">
              {group.description}
            </p>
          )}
        </div>
      </div>

      <hr className="my-4 border-gray-200" />
      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <div className="text-subdued">Group Members</div>
          <div className="font-medium">{group.membersCount}</div>
        </div>
        <div className="flex justify-between">
          <div className="text-subdued">Bookmarks</div>
          <div className="font-medium">
            {group.viewerIsMember ? group.bookmarksVisibleCount : group.bookmarksCount}
          </div>
        </div>
      </div>

      {showFooter && (
        <>
          <hr className="my-4 border-gray-200" />
          <div className="-mx-3 -mb-2 -mt-3 flex items-center justify-between">
            <Button variant="ghost" size="sm" asChild className="text-xs hover:bg-transparent">
              <Link to={groupDetailPath({ id: group.id })} onClick={(e) => e.stopPropagation()}>
                View Details
              </Link>
            </Button>
            <div className="flex items-center gap-2 text-xs">
              {group.visibility === "PUBLIC" && !group.viewerIsMember && (
                <JoinButton group={group} />
              )}
              {group.viewerIsMember && (
                <Button variant="ghost" size="sm" className="gap-2 text-xs" disabled={true}>
                  <Check className="h-4 w-4" />
                  Member
                </Button>
              )}
              {group.viewerIsMember && <InviteButton group={group} className="text-xs" />}
            </div>
          </div>
        </>
      )}
    </Link>
  )
}
